import graphql__e77f4181fbeb0f66406a from "./__generated__/CityWalkWaypoint_cityWalkWaypoint.graphql.js";import { useCallback, useEffect } from "react";
import { graphql, useFragment } from "react-relay";
import { useDispatch, useSelector } from "react-redux";
import { Outlet } from "react-router";
import { defineMessages } from "react-intl";
import classNames from "classnames";

import Button from "../Buttons/Button";
import { useFormatMessageWithCustomization } from "../Intl/Context";
import CityWalkTimeline from "./CityWalkTimeline";
import { useNavigateTo } from "../Routes/RoutesContext";
import MapPanelActions from "../Maps/MapPanel/MapPanelActions";
import MapElementImage from "../Maps/MapElementImage";
import { useMoveMapCenterTo } from "../Maps/moveMapCenterTo";
import { useMapContext } from "../Maps/MapContext";
import { useMapPanel } from "../Maps/MapPanel/withMapPanel";
import Sidebar from "../Sidebar/Sidebar";

import {
  setCityWalkCurrentWaypoint,
  setCityWalkCompleteDialogActive,
  setCityWalkActive
} from "../../reducers/cityWalks";
import { setMapPanelMapElement } from "../../reducers/map/panels";
import { completeCityWalkContribution } from "../../mutations/CompleteCityWalkContributionMutation";
import { useDeferredEffect } from "../Utils/useDeferredEffect";
import { isUnauthorized } from "../Utils/authorizations";

const CityWalkWaypoint = props => {
  const cityWalkWaypoint = useFragment(
    CityWalkWaypoint_cityWalkWaypoint,
    props.cityWalkWaypoint
  );
  const { id, title, isFilled, isFillable, element, nextWaypointId, sidebar } =
    cityWalkWaypoint;
  const {
    slug: participativeModuleSlug,
    waypoints,
    activeContribution,
    cityWalkWaypointsDefaultZoom
  } = cityWalkWaypoint.cityWalk;
  const { contributionsSubmissionAuthorized } = cityWalkWaypoint.cityWalk.authorizations;
  const { latitude, longitude } = cityWalkWaypoint.element;
  const { id: panelId } = useMapPanel();

  const { mapKey } = useMapContext();
  const isLastWaypoint = useSelector(
    ({ map }) => cityWalkWaypoint.waypointIndex == map[mapKey].cityWalk.waypointsCount - 1
  );

  const translateWithCustomization = useFormatMessageWithCustomization(
    cityWalkWaypoint.cityWalk.customIntlMessages
  );
  const contribute = translateWithCustomization(intlMessages.contribute);
  const nextWaypoint = translateWithCustomization(intlMessages.nextWaypoint);
  const finalizeCityWalk = translateWithCustomization(intlMessages.finalizeCityWalk);

  // Recenter map after the panel content is loaded
  const moveMapCenterTo = useMoveMapCenterTo();

  useDeferredEffect(
    () => {
      if (!latitude || !longitude) return;

      moveMapCenterTo(latitude, longitude, cityWalkWaypointsDefaultZoom);
    },
    [latitude, longitude, cityWalkWaypointsDefaultZoom, moveMapCenterTo],
    { wait: 500 }
  );

  const waypointElement = cityWalkWaypoint.element;
  const dispatch = useDispatch();

  // Ensure the current waypoint is selected
  useEffect(() => {
    dispatch(setCityWalkActive(mapKey, true));
    dispatch(setCityWalkCurrentWaypoint(mapKey, { id }));
    dispatch(setMapPanelMapElement(mapKey, panelId, waypointElement));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, waypointElement]);

  const nextButtonClassName = classNames([
    "btn",
    "btn-block-md",
    {
      "btn-primary-inverse": !isFilled,
      "btn-primary": isFilled
    }
  ]);

  const { iconStyle } = element.style || {};

  const icon = iconStyle && (
    <span
      className="d-flex float-left text-left padding-right-xs"
      dangerouslySetInnerHTML={{
        __html: MapElementImage({
          ...iconStyle,
          width: 40,
          height: 40,
          position: "static"
        })
      }}
    />
  );

  const navigateTo = useNavigateTo();

  const handleContributeButtonClicked = useCallback(() => {
    navigateTo("cityWalkWaypointContributionForm", {
      participativeModuleSlug,
      cityWalkWaypointId: id
    });
  }, [participativeModuleSlug, id, navigateTo]);

  const handleNextWaypointButtonClicked = useCallback(() => {
    const cityWalkWaypointId = nextWaypointId;

    dispatch(setCityWalkCurrentWaypoint(mapKey, { id: cityWalkWaypointId }));

    navigateTo("cityWalkWaypoint", {
      participativeModuleSlug,
      cityWalkWaypointId
    });
  }, [participativeModuleSlug, nextWaypointId, dispatch, mapKey, navigateTo]);

  const handleCompleteButtonClick = useCallback(() => {
    if (activeContribution && activeContribution.didCompleteAllWaypoints) {
      completeCityWalkContribution(activeContribution);
    } else {
      dispatch(setCityWalkCompleteDialogActive(mapKey, true));
    }
  }, [activeContribution, dispatch, mapKey]);

  const displayContributionButton =
    !isUnauthorized(contributionsSubmissionAuthorized) && isFillable && !isFilled;

  return (
    <>
      <div className="map-panel-header box padding-sm d-flex align-items-center city-walk-waypoint-panel">
        <div className="map-panel-title-wrapper flex-1">
          <h1 className="text-title-page d-flex align-items-center" aria-live="assertive">
            {icon}
            <span className="flex-1">{title}</span>
          </h1>
        </div>
      </div>

      <CityWalkTimeline waypoints={waypoints} />

      <Sidebar sidebar={sidebar} />

      <MapPanelActions>
        {displayContributionButton && (
          <Button
            type="button"
            className="btn btn-block-md btn-primary"
            onClick={handleContributeButtonClicked}
            label={contribute}
            iconClassName="icon-arrow-right"
            iconPosition="after"
            data-contribute-button
          />
        )}

        {!isLastWaypoint && (
          <Button
            type="button"
            className={nextButtonClassName}
            onClick={handleNextWaypointButtonClicked}
            label={nextWaypoint}
            iconClassName="icon-arrow-right"
            iconPosition="after"
            data-city-walk-waypoint-next-waypoint-button
          />
        )}

        {(isFilled || isLastWaypoint) && activeContribution && (
          <Button
            type="button"
            className="btn btn-block-md btn-primary-inverse"
            onClick={handleCompleteButtonClick}
            label={finalizeCityWalk}
            iconClassName="icon-arrow-right"
            iconPosition="after"
            data-city-walk-waypoint-complete-button
          />
        )}
      </MapPanelActions>

      <Outlet />
    </>
  );
};

const CityWalkWaypoint_cityWalkWaypoint = graphql__e77f4181fbeb0f66406a;

export default CityWalkWaypoint;

const intlMessages = defineMessages({
  contribute: {
    id: "city_walk.waypoint.contribute",
    defaultMessage: "Donner mon avis sur ce point",
    description: "Libellé du bouton pour contribuer à une étape de la balade"
  },
  nextWaypoint: {
    id: "city_walk.waypoint.nextWaypoint",
    defaultMessage: "Poursuivre la balade",
    description: "Libellé du bouton pour aller à l'étape suivante de la balade"
  },
  finalizeCityWalk: {
    id: "city_walk.waypoint.finalizeCityWalk",
    defaultMessage: "Terminer la balade",
    description: "Libellé du bouton pour terminer la balade"
  }
});
